// docs/29 in the encounter HUD: the encounter modifiers this fight's sky and land put in force.
// Three places, each quiet until it is needed:
// - the FIELD panel's rows (glyph, name, the rule in a few words; hover or focus opens the full
//   rule and why it holds);
// - a cue on the board's edge for the two that have a direction (the low sun on its side, the
//   wind's arrow along the top), so "toward the sun" and "downwind" can be read off the board;
// - a card over the board when the fight opens (and whenever the set changes) listing them once,
//   gone after a few seconds or on click.
// Every word is the sim's (`ModifierNote`); this file only lays them out.
import { useEffect, useState } from 'react';
import { Reveal, afterMotion } from '../components';
import { useView } from '../../core/stores/view';
import { useEncounter } from '../../core/encounter/session';
import { arrow, glyphOf, modifiersKey } from '../../core/sim/modifiers';
import { DEFAULT_CELL } from '../../config';
import type { ModifierNote } from '../../core/sim/types';

/** The FIELD panel's modifier rows. */
export function ModifierRows({ notes }: { notes: readonly ModifierNote[] }) {
  if (!notes.length) return null;
  return (
    <ul className="fk-hud__mods" data-testid="hud-modifiers">
      {notes.map((n) => (
        <li key={n.key} className="fk-hud__mod" tabIndex={0} data-testid="hud-modifier" data-key={n.key} aria-label={`${n.name}: ${n.rule} ${n.why}`}>
          <span className="fk-hud__mod-glyph" aria-hidden="true">{glyphOf(n.modifier)}{n.modifier === 'Wind' ? arrow(n.toward) : ''}</span>
          <span className="fk-hud__mod-name">{n.name}</span>
          <span className="fk-hud__mod-short">{n.short}</span>
          <span className="fk-hud__mod-more" role="tooltip">
            <span className="fk-hud__mod-rule">{n.rule}</span>
            <span className="fk-hud__mod-why">{n.why}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

/**
 * The low sun on the board's east or west edge and the wind's arrow on its top edge (docs/29 X8),
 * outside the board so they never sit on a square. Hover for the rule.
 */
export function EdgeCues() {
  const field = useView((s) => s.field);
  const w = useEncounter((s) => s.snapshot?.board.w ?? 0);
  const h = useEncounter((s) => s.snapshot?.board.h ?? 0);
  const zoom = useView((s) => s.zoom);
  const scroll = useView((s) => s.scroll);
  const viewport = useView((s) => s.viewport);
  const sun = field?.modifiers.find((m) => m.modifier === 'LowSun');
  const wind = field?.modifiers.find((m) => m.modifier === 'Wind');
  if (!viewport || !w || (!sun && !wind)) return null;
  // the board's rectangle in the stage, as TargetPreview places its card
  const px = DEFAULT_CELL * zoom;
  const left = Math.round(-scroll.x * zoom) - viewport.x;
  const top = Math.round(-scroll.y * zoom) - viewport.y;
  const right = left + w * px;
  const mid = top + (h * px) / 2;
  // when the board fills the viewport the cue tucks inside its edge rather than over the side columns
  const east = right + 6 + 48 <= viewport.w ? right + 6 : viewport.w - 4;
  const west = left - 6 - 48 >= 0 ? left - 6 : 4;
  const inside = (x: number, edge: number) => (x === edge ? '' : ' fk-hud__edge--inside');
  return (
    <>
      {sun && (
        <span className={`fk-hud__edge fk-hud__edge--sun fk-hud__edge--${sun.toward > 0 ? 'east' : 'west'}${sun.toward > 0 ? inside(east, right + 6) : inside(west, left - 6)}`} data-testid="edge-sun" data-toward={sun.toward}
          style={{ left: sun.toward > 0 ? east : west, top: mid }} title={`${sun.name}: ${sun.rule}\n${sun.why}`}>
          <span className="fk-hud__edge-glyph" aria-hidden="true">☀</span>
          <span className="fk-hud__edge-word">{sun.toward > 0 ? 'east' : 'west'}</span>
        </span>
      )}
      {wind && (
        <span className="fk-hud__edge fk-hud__edge--wind" data-testid="edge-wind" data-toward={wind.toward}
          style={{ left: Math.max(80, Math.min(viewport.w - 80, (left + right) / 2)), top: Math.max(30, top - 6) }} title={`${wind.name}: ${wind.rule}\n${wind.why}`}>
          <span className="fk-hud__edge-glyph" aria-hidden="true">{wind.toward < 0 ? '←≋' : '≋→'}</span>
          <span className="fk-hud__edge-word">{wind.short}</span>
        </span>
      )}
    </>
  );
}

const CARD_HOLD_MS = 9000;
const seenKey = (enc: bigint | undefined, key: string) => `fk.mods.${enc ?? 'local'}.${key}`;
const seen = (k: string) => { try { return sessionStorage.getItem(k) === '1'; } catch { return false; } };
const markSeen = (k: string) => { try { sessionStorage.setItem(k, '1'); } catch { /* private mode */ } };

/**
 * "Under this sky": the modifiers in force, once per encounter and set (docs/29 X8), over the top
 * of the board. Holds `hold` ms (default nine seconds), then fades; a click dismisses it.
 */
export function ModifierCard({ hold = CARD_HOLD_MS, suppressed = false }: { hold?: number; suppressed?: boolean }) {
  const notes = useView((s) => s.field?.modifiers);
  const encounterId = useEncounter((s) => s.encounterId);
  const key = notes?.length ? seenKey(encounterId, modifiersKey(notes)) : undefined;
  const [shown, setShown] = useState<string>();
  const [leaving, setLeaving] = useState(false);
  useEffect(() => { if (key && !suppressed && !seen(key)) { markSeen(key); setShown(key); setLeaving(false); } }, [key, suppressed]);
  useEffect(() => { if (!shown || leaving) return; const t = setTimeout(() => setLeaving(true), hold); return () => clearTimeout(t); }, [shown, leaving, hold]);
  useEffect(() => (leaving ? afterMotion(220, () => setShown(undefined)) : undefined), [leaving]);
  if (!shown || shown !== key || !notes?.length) return null;
  return (
    // docs/83: it goes by itself and never takes a click meant for the board under it
    <Reveal kind="pop" className={`fk-hud__modcard ${leaving ? 'fk-hud__modcard--leaving' : ''}`} role="status" data-testid="modifier-card">
      <span className="fk-hud__modcard-eyebrow">Under this sky</span>
      <ul className="fk-hud__modcard-list">
        {notes.map((n) => (
          <li key={n.key}><span className="fk-hud__mod-glyph" aria-hidden="true">{glyphOf(n.modifier)}{n.modifier === 'Wind' ? arrow(n.toward) : ''}</span> <b>{n.name}</b> — {n.rule}</li>
        ))}
      </ul>
      <span className="fk-hud__modcard-foot">Listed under FIELD; hover a row for why.</span>
    </Reveal>
  );
}
